'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'warning' | 'info' | 'success' | 'danger';
  time: string;
  read: boolean;
  actionLabel?: string;
  actionHref?: string;
  category: string;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    title: 'TDS Return Due in 15 Days',
    message: 'Your Q3 TDS return (Form 24Q) is due on 7 September 2026. File early to avoid penalties.',
    type: 'warning',
    time: '2 hours ago',
    read: false,
    actionLabel: 'View Compliance',
    actionHref: '/compliance',
    category: 'Compliance',
  },
  {
    id: '2',
    title: 'Trademark Objection Filed',
    message: 'An objection has been raised against your ELYSIUM trademark application. Response due in 30 days.',
    type: 'danger',
    time: '5 hours ago',
    read: false,
    actionLabel: 'Manage Objection',
    actionHref: '/trademark/status',
    category: 'Trademark',
  },
  {
    id: '3',
    title: 'Documents Pending Upload',
    message: '3 documents are still required to complete your company registration: Director 2 PAN, Utility Bill, NOC.',
    type: 'info',
    time: '1 day ago',
    read: false,
    actionLabel: 'Upload Now',
    actionHref: '/documents',
    category: 'Registration',
  },
  {
    id: '4',
    title: 'Pre-Filing Review Ready',
    message: 'Your company incorporation application is 94% complete. Review and approve before AI files.',
    type: 'info',
    time: '1 day ago',
    read: false,
    actionLabel: 'Review & Approve',
    actionHref: '/review',
    category: 'Registration',
  },
  {
    id: '5',
    title: 'MSME Certificate Issued',
    message: 'Your Udyam Registration Certificate has been issued. Udyam No: UDYAM-HR-12-0034521.',
    type: 'success',
    time: '3 days ago',
    read: true,
    actionLabel: 'View Certificate',
    actionHref: '/msme',
    category: 'MSME',
  },
  {
    id: '6',
    title: 'AI Analysis Complete',
    message: 'Trademark risk analysis for ELYSIUM FRAGRANCES is complete. Risk score: 62/100 (Moderate).',
    type: 'info',
    time: '4 days ago',
    read: true,
    actionLabel: 'View Analysis',
    actionHref: '/trademark',
    category: 'Trademark',
  },
];

const typeConfig = {
  warning: { dot: 'bg-warning', bg: 'bg-warning/8', border: 'border-warning/20', icon: '⚠', iconColor: 'text-warning' },
  info: { dot: 'bg-ai', bg: 'bg-ai/5', border: 'border-ai/15', icon: 'ℹ', iconColor: 'text-ai' },
  success: { dot: 'bg-success', bg: 'bg-success/8', border: 'border-success/20', icon: '✓', iconColor: 'text-success' },
  danger: { dot: 'bg-danger', bg: 'bg-danger/8', border: 'border-danger/20', icon: '!', iconColor: 'text-danger' },
};

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

export default function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const dismiss = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const displayed = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-[150] bg-ink/20 backdrop-blur-[2px]" onClick={onClose} />
      )}

      {/* Panel */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white z-[160] shadow-2xl border-l border-border flex flex-col transition-transform duration-300 ease-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-ai-light rounded-lg flex items-center justify-center border border-ai/20">
              <svg className="w-4 h-4 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <h2 className="text-[14px] font-bold text-ink">Notifications</h2>
              {unreadCount > 0 && (
                <p className="text-[11px] text-ink-muted">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] font-medium text-ai hover:text-ai-dark transition-colors"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 transition-colors text-ink-muted"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 px-5 py-3 border-b border-border flex-shrink-0">
          {(['all', 'unread'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors capitalize ${
                filter === f ? 'bg-ai-light text-ai' : 'text-ink-muted hover:text-ink hover:bg-surface-2'
              }`}
            >
              {f === 'all' ? `All (${notifications.length})` : `Unread (${unreadCount})`}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-14 h-14 bg-surface-3 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <p className="text-[13px] font-medium text-ink mb-1">All caught up</p>
              <p className="text-[12px] text-ink-muted">No {filter === 'unread' ? 'unread ' : ''}notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {displayed.map(notif => {
                const cfg = typeConfig[notif.type];
                return (
                  <div
                    key={notif.id}
                    className={`relative px-5 py-4 transition-colors ${!notif.read ? cfg.bg : 'hover:bg-surface-2'}`}
                    onClick={() => markRead(notif.id)}
                  >
                    {!notif.read && (
                      <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    )}
                    <div className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 text-[12px] font-bold border ${cfg.bg} ${cfg.border} ${cfg.iconColor}`}>
                        {cfg.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className={`text-[13px] font-semibold leading-tight ${!notif.read ? 'text-ink' : 'text-ink-muted'}`}>
                            {notif.title}
                          </p>
                          <button
                            onClick={e => { e.stopPropagation(); dismiss(notif.id); }}
                            className="text-ink-subtle hover:text-ink transition-colors flex-shrink-0 mt-0.5"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-[12px] text-ink-muted leading-relaxed mb-2">{notif.message}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-semibold text-ink-subtle bg-surface-3 border border-border px-1.5 py-0.5 rounded-md">
                              {notif.category}
                            </span>
                            <span className="text-[10px] text-ink-subtle">{notif.time}</span>
                          </div>
                          {notif.actionHref && (
                            <Link
                              href={notif.actionHref}
                              onClick={e => { e.stopPropagation(); markRead(notif.id); onClose(); }}
                              className="text-[11px] font-semibold text-ai hover:text-ai-dark transition-colors"
                            >
                              {notif.actionLabel} →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-border flex-shrink-0">
          <p className="text-[11px] text-ink-subtle text-center">
            Notifications are updated in real-time · <button className="text-ai hover:text-ai-dark transition-colors">Manage preferences</button>
          </p>
        </div>
      </div>
    </>
  );
}
